// Operator voiced insight articles. Categories drive filter chips on the index page.
export const CATEGORIES = [
  { slug: "operations", label: "Operations" },
  { slug: "labour", label: "Labour" },
  { slug: "performance", label: "Performance" },
  { slug: "guest-experience", label: "Guest Experience" },
];

export const INSIGHTS = [
  {
    slug: "pricing-decisions-written-down-beat-pricing-decisions-remembered",
    title: "Pricing decisions written down beat pricing decisions remembered.",
    category: "performance",
    excerpt:
      "Most independent hotels do not have a pricing problem. They have a pricing memory problem. The decisions are being made — they just are not written down, which is why the operating team cannot defend them and the owner cannot review them.",
    readMinutes: 6,
    publishedAt: "2026-02-26",
    body: [
      {
        h: "The conversation that never gets written down",
        p: "Walk into the revenue meeting at most independent hotels and the conversation is sharp. The team know the comp set. They know what last weekend did. They know what's on the books for next month. Decisions get taken — close out a rate plan, open up corporate, hold rate against a weak Sunday — and the meeting moves on. The decisions are real. They just are not written down. Two weeks later, nobody can quite remember why the Thursday rate was held when occupancy was sitting at 62 per cent.",
      },
      {
        h: "Why this matters commercially",
        p: "Pricing decisions that are not written down cannot be reviewed. The team has no learning loop. The owner has no visibility. The asset manager has no audit trail. When the comp set moves and you need to explain why your strategy held, nobody can. The next time the same situation comes up, the operating team starts from scratch — and the second decision is rarely as sharp as the first.",
      },
      {
        h: "What a written pricing decision actually looks like",
        p: "Three lines is enough. The decision (held BAR at £189 across Thursday to Sunday). The reason (comp set softening but our segmentation suggests the corporate base will hold). The expected outcome (occupancy 68 to 74 per cent, ADR holding, RevPAR up 4 to 7 per cent versus same period last year). At the end of the week, one line on what actually happened and what we learned. Nothing more. Nothing complicated.",
      },
      {
        h: "Who owns the log",
        p: "The revenue manager writes it. The general manager signs it off. The owner sees it monthly. The asset manager sees it quarterly. The log is the asset, not any individual entry. After three months, the team has a real evidence base. After twelve, the team has a competitive advantage — because most independent hotels still have not started doing this.",
      },
      {
        h: "The version of this that is not pricing",
        p: "Once the pricing version is running, the same discipline applies to every operational decision worth defending. Why we held the rota at 38 covers per chef instead of cutting. Why we closed the bar at 10pm on weekdays. Why we kept the supplier despite a 4 per cent price increase. None of these decisions are wrong. They just need to be defensible — and that requires a paper trail.",
      },
      {
        h: "The cheapest competitive advantage in hospitality",
        p: "Operational decisions that are written down, reviewed and learned from compound into operational judgement that does not. That is the cheapest competitive advantage available to an independent hotel right now, and the one most often left on the floor.",
      },
    ],
  },
  {
    slug: "why-your-stock-count-is-the-cheapest-performance-lever-you-have",
    title: "Why your stock count is the cheapest performance lever you have.",
    category: "performance",
    excerpt:
      "Most general managers think the stock count is a finance exercise. It is not. It is the cheapest operational diagnostic available, and most hotels do it badly enough that two thirds of the signal is being lost.",
    readMinutes: 6,
    publishedAt: "2026-02-22",
    body: [
      {
        h: "Why the stock count gets dismissed",
        p: "The monthly stock count gets a bad reputation because it is slow, tedious and usually run by the financial controller rather than the operations team. The numbers go into the management accounts, the variance is signed off as immaterial, and the operating team carries on. Nobody is wrong, exactly. But the operation has just missed the cheapest performance diagnostic available to it that month.",
      },
      {
        h: "What the count is actually telling you",
        p: "A clean stock count is a forensic snapshot of the operation. It tells you whether the head chef's plate cost is real or theoretical. It tells you whether the food and beverage manager's portion control is being enforced. It tells you whether the receiving paperwork matches what is on the shelves. It tells you whether yield on a banquet was what the costing said. None of these signals show up in the P&L on their own. They show up in the count.",
      },
      {
        h: "Why most counts are too quiet to be useful",
        p: "Most hotels run the count quarterly, leave the variance investigation to finance, and accept anything under 1 to 2 per cent as noise. That is too quiet. A monthly count with department level granularity, owned by the head chef and food and beverage manager rather than the financial controller, surfaces operational drift two or three months before it becomes a margin problem.",
      },
      {
        h: "The four small changes that unlock the lever",
        p: "Move the count cadence to monthly. Break the variance down by category and by department. Make the head chef and food and beverage manager sign off the variance, not finance. And introduce a 30 minute variance meeting the day after the count, with named actions and a closing date. That is it. None of these changes cost anything.",
      },
      {
        h: "What changes once it runs",
        p: "Within a quarter the variance investigation surfaces specific operational drift — a portion size, a supplier substitution, a banquet yield, a missed delivery. The head chef and food and beverage manager start running the operation against the count rather than against memory. The financial controller has cleaner numbers to defend at month end. The owner has earlier sight of margin movement than the P&L alone can give them.",
      },
      {
        h: "Why the cheapest lever is also the most ignored",
        p: "Because it is unglamorous. The operating team would rather talk about menu engineering, supplier rationalisation or a new revenue management system. All of those are worthwhile. But the stock count is sitting on the desk waiting to be picked up, costs nothing, and most hotels are still running it as a finance task rather than an operational diagnostic. That is the gap.",
      },
    ],
  },
  {
    slug: "why-most-pre-opening-budgets-overrun",
    title: "Why most pre-opening budgets overrun. And how to stop it.",
    category: "operations",
    excerpt:
      "Pre-opening budgets are not lost in big numbers. They are lost in dozens of small decisions taken without a sequenced operating plan. A controlled launch costs less than a recovery exercise.",
    readMinutes: 6,
    publishedAt: "2026-02-19",
    body: [
      {
        h: "The shape of the typical overrun",
        p: "Most pre-opening budget overruns do not come from a single bad call. They come from compound small decisions: a second supplier engaged because the first is slow, a position hired three weeks early to be safe, a soft launch extended because the team is not ready, a snagging budget topped up because the contractor missed a list. None of these alone are wrong. Together, they are the overrun.",
      },
      {
        h: "Sequence is the lever",
        p: "The single biggest determinant of whether a pre-opening lands on budget is the sequence of decisions, not the size of the budget. A property that hires front office two months before the contractor releases the desk is paying for capacity it cannot use. A property that orders linen before the laundry contract is signed is paying for storage it did not plan to need. A clear operating sequence — operating workstreams, decision points, hiring sequence, training cadence — closes most of the leakage.",
      },
      {
        h: "What ownership should see, month by month",
        p: "Ownership needs a single page status pack each month: workstreams on, off and at risk; budget variance by category; named decision points coming up. If the operating team cannot produce that in a single page, the operation is being run reactively, and reactive pre-openings overspend.",
      },
      {
        h: "When to engage independent help",
        p: "External pre-opening support is most useful nine to twelve months out, when the operating spine is still being written rather than being defended. Engagements after the team is in place are still useful, but the upside compounds with lead time.",
      },
    ],
  },
  {
    slug: "the-owners-monthly-pack-trap",
    title: "The owner's monthly pack is the wrong document.",
    category: "performance",
    excerpt:
      "Most monthly management packs tell the owner what happened, not what to do about it. The fix is structural, not editorial. Reporting should prompt operational action, not measure activity.",
    readMinutes: 5,
    publishedAt: "2026-02-15",
    body: [
      {
        h: "The pack that arrives but does not move anything",
        p: "Walk into most independent hotels and the monthly management pack tells a familiar story. Revenue versus budget. GOP versus budget. ADR, occupancy, RevPAR, all benchmarked. Maybe a paragraph of commentary written under time pressure. The numbers are correct. The pack is internally consistent. And nothing in it will move next month, because the document was designed to report, not to direct.",
      },
      {
        h: "What an operating pack should do instead",
        p: "An operating pack should answer three questions. What happened. Why it happened. What is being done about it. The first is reporting. The second is diagnosis. The third is operational commitment, with a named owner and a date. If the third section is the shortest, the pack is documenting activity rather than driving it.",
      },
      {
        h: "Variance commentary belongs with the head of department, not finance",
        p: "Finance can flag the variance. Only the head of department who runs the line can say why it moved and what they are going to do. Variance commentary signed off by the food and beverage manager or the rooms division manager is far more useful than commentary written by the financial controller, however well drafted.",
      },
      {
        h: "Cadence beats redesign",
        p: "Most owners do not need a new pack. They need a published cadence: same agenda every month, same authors, same action log carried forward, same decision points scheduled. The pack will improve naturally once the conversation around it is structured.",
      },
    ],
  },
  {
    slug: "labour-cost-is-not-the-problem",
    title: "Labour cost is not the problem. The rota is.",
    category: "labour",
    excerpt:
      "Most hotels with a labour cost issue do not have a wage issue. They have a rostering issue. The labour line gets squeezed in the wrong places, the right places stay over-resourced, and service suffers as if cost had never moved at all.",
    readMinutes: 5,
    publishedAt: "2026-02-12",
    body: [
      {
        h: "Where the leakage actually sits",
        p: "Walk into most hotels at 8am on a Tuesday and the labour line is being run on autopilot. The rota was written off last week's rota, which was written off the one before, which was originally written for a property whose business mix has long since changed. Productivity targets, when they exist at all, were set against a benchmark from a previous role.",
      },
      {
        h: "The four checks that actually move it",
        p: "A useful rota is built against forecast occupancy, food and beverage covers, conversion and the operating standards you need to hold. Each department has its own productivity benchmark. Agency and overtime exposure is reviewed weekly with named accountability. Role design is revisited annually, not assumed.",
      },
      {
        h: "Why this is not a finance project",
        p: "Labour is operational. Finance can flag the variance, but only operations can fix it. The fastest improvements come from changing the conversation at the operations meeting, not the conversation at month end.",
      },
    ],
  },
  {
    slug: "the-90-day-test",
    title: "The 90 day test: would a new owner spot the same problems?",
    category: "operations",
    excerpt:
      "If you sold the property tomorrow and the new owner walked the property on day one, day thirty and day ninety, would they reach the same conclusions you have? If not, your operating story and your operating reality are diverging.",
    readMinutes: 4,
    publishedAt: "2026-02-08",
    body: [
      {
        h: "Operating story versus operating reality",
        p: "Every hotel team has an operating story. The standards are tight. The guests are happy. The labour line is under control. The kitchen runs well. Most of the time the story is broadly true. Sometimes it has drifted, and the drift is invisible from the inside because it happened gradually.",
      },
      {
        h: "The walkthrough that closes the gap",
        p: "Quarterly operational reviews, ideally independent of the operating team, are the cheapest way to keep the story and the reality close together. Three weeks. A property visit. A written report. No team consultation hours, no slide deck. Just findings.",
      },
      {
        h: "When ownership is hands off",
        p: "If an owner is not running the property day to day, the cadence matters even more. Reporting from the operating team is necessary but not sufficient. Independent visibility, on the owner's terms, is the difference between knowing what is happening and being told what is happening.",
      },
    ],
  },
  {
    slug: "guest-experience-is-a-rota-decision",
    title: "Guest experience is a rota decision, not a values poster.",
    category: "guest-experience",
    excerpt:
      "Putting service values on the back of house wall does not lift the score on Booking.com. Service is engineered, shift by shift, by the rota, the standards and the operating cadence behind them.",
    readMinutes: 4,
    publishedAt: "2026-02-04",
    body: [
      {
        h: "Where scores actually come from",
        p: "Guest scores move when the operational moments that matter to the guest are covered properly. Check in within three minutes of arrival. Housekeeping rooms ready against the arrival pattern. Food and beverage service at the right pace for the time of day. These are operational decisions, not cultural decisions.",
      },
      {
        h: "The audit cadence that keeps it honest",
        p: "Monthly quality assurance audits, owned by HODs and reviewed by the general manager, are the discipline that prevents scores drifting. The audit itself does not improve the score. The follow up action does.",
      },
    ],
  },
  {
    slug: "your-best-month-is-the-warning-sign",
    title: "Your best month is the warning sign.",
    category: "performance",
    excerpt:
      "Hotels that are running well do not need a turnaround. They need to identify the levers that took the operation from average to good, so the operation does not regress when the trading environment shifts.",
    readMinutes: 3,
    publishedAt: "2026-01-29",
    body: [
      {
        h: "Read the strong months as carefully as the weak ones",
        p: "When a month delivers, most operating teams move on. The far more useful exercise is to disaggregate which levers actually produced the lift. Was it rate? Mix? Cost discipline? Front office conversion? A new HOD? Without that disaggregation, the lift is not repeatable.",
      },
      {
        h: "Building the playbook from the upside",
        p: "Performance reviews tend to focus on the variance against budget. The more useful framing is variance against the operation's own best month. That comparison surfaces the operational practice the team should institutionalise.",
      },
    ],
  },
  {
    slug: "operating-cadence-beats-strategy",
    title: "Operating cadence beats strategy.",
    category: "operations",
    excerpt:
      "Most properties that are underperforming do not lack strategy. They lack cadence. A weekly trading review and a monthly P&L walkthrough will outperform a strategy off site, every time.",
    readMinutes: 4,
    publishedAt: "2026-01-22",
    body: [
      {
        h: "Cadence as an operating system",
        p: "A property running well has a rhythm: daily HOD stand-up at a fixed time, a weekly commercial and trading review, a monthly P&L walkthrough, a quarterly performance review. Each one has a published agenda, named owner and visible action log.",
      },
      {
        h: "What strategy off sites do not fix",
        p: "Two day off sites produce documents. They rarely change Monday morning. The cheapest, highest leverage operational change in most hotels is to publish the cadence, hold the meetings, close the action log and repeat it for three months.",
      },
    ],
  },
  {
    slug: "food-and-beverage-margin-leaks-quietly",
    title: "Food and beverage margin leaks quietly.",
    category: "performance",
    excerpt:
      "Rooms division losses are loud. Food and beverage losses are quiet. Menu engineering, plate cost, wastage and stock count discipline each move the margin by 50 to 150 basis points. Together they decide the contribution.",
    readMinutes: 5,
    publishedAt: "2026-01-15",
    body: [
      {
        h: "The four levers that matter",
        p: "Menu engineering every six months against star, plough, puzzle, dog. Plate cost recalculated quarterly against current supplier pricing. Wastage logged at department level and reviewed weekly. Stock counts completed monthly with variance investigated by the head chef.",
      },
      {
        h: "Why this is a head chef conversation",
        p: "Food and beverage margin is owned operationally by the head chef and the food and beverage manager. Finance can report it. Only operations can move it. The most reliable lift comes from rebuilding the operating cadence around those four levers.",
      },
    ],
  },
];

export const INSIGHTS_BY_SLUG = INSIGHTS.reduce((acc, a) => {
  acc[a.slug] = a;
  return acc;
}, {});
